import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import type { BaseInputProps, SearchOption } from "@/shared/types";
import clsx from "clsx";
import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Controller, useWatch, type FieldPath, type FieldValues } from "react-hook-form";
import { useDebounce } from "../lib/useDebounce";
import { inputClassName } from "./TextInput";
import { WrapperInput } from "./WrapperInput";

type SearchInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string = string,
> = BaseInputProps<TFieldValues, TName> & {
  options: SearchOption<TValue>[];
  noResultsText?: string;
  onOptionSelect?: (option: SearchOption<TValue>) => void;
  debounceDelay?: number;
};

export function SearchInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string = string,
>({
  control,
  name,
  label,
  placeholder = "Search option",
  description,
  disabled,
  required,
  className,
  options,
  noResultsText = "No matches found",
  onOptionSelect,
  debounceDelay = 300,
}: SearchInputProps<TFieldValues, TName, TValue>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedValue = useWatch({ control, name });
  const inputClasses = getThemeComponentGroup("input");
  const selectClasses = getThemeComponentGroup("select");
  const debouncedQuery = useDebounce(query, debounceDelay);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    const selected = options.find((option) => option.value === selectedValue);
    setQuery(selected?.label ?? String(selectedValue ?? ""));
  }, [options, selectedValue]);

  useEffect(() => {
    if (!isTyping) {
      setOpen(false);
      return;
    }

    setOpen(debouncedQuery.trim().length > 0);
  }, [debouncedQuery, isTyping]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const normalized = debouncedQuery.trim().toLowerCase();
        const filteredOptions = normalized
          ? options
              .filter((option) =>
                [option.label, ...(option.keywords ?? [])]
                  .join(" ")
                  .toLowerCase()
                  .includes(normalized)
              )
              .slice(0, 20)
          : [];

        return (
          <WrapperInput
            label={label}
            error={fieldState.error?.message}
            description={description}
            required={required}
            className={className}
          >
            <div ref={containerRef} className={clsx("relative", open && "z-50")}>
              <Search
                className={clsx(
                  "pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2",
                  getThemeClass(
                    selectClasses.placeholderColor || inputClasses.placeholderColor,
                    "text-theme-text-soft"
                  )
                )}
              />
              <input
                value={query}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={() => {
                  if (isTyping && debouncedQuery.trim()) {
                    setOpen(true);
                  }
                }}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setIsTyping(true);
                  setQuery(nextValue);
                  field.onChange("");

                  if (!nextValue.trim()) {
                    setOpen(false);
                  }
                }}
                onBlur={() => field.onBlur()}
                className={clsx(
                  inputClassName,
                  "pl-11",
                  getThemeClass(
                    selectClasses.borderColor || inputClasses.borderColor,
                    "border-theme-border-strong"
                  ),
                  getThemeClass(selectClasses.bgColor || inputClasses.bgColor, "bg-theme-surface"),
                  getThemeClass(
                    selectClasses.textColor || inputClasses.textColor,
                    "text-theme-text"
                  ),
                  getThemeClass(
                    selectClasses.placeholderColor || inputClasses.placeholderColor,
                    "placeholder:text-theme-text-placeholder"
                  ),
                  getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
                  "focus:ring-2",
                  getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
                  inputClasses.base,
                  fieldState.error &&
                    clsx(
                      inputClasses.errorBorderColor || "border-theme-danger-bright",
                      "focus:ring-theme-danger-bright/10"
                    )
                )}
              />

              {open ? (
                <div
                  className={clsx(
                    "absolute z-40 mt-2 max-h-64 w-full overflow-auto thin-scrollbar rounded-lg border shadow-lg",
                    getThemeClass(
                      selectClasses.borderColor || inputClasses.borderColor,
                      "border-theme-border"
                    ),
                    getThemeClass(
                      selectClasses.bgColor || inputClasses.bgColor,
                      "bg-theme-surface"
                    ),
                    selectClasses.menu
                  )}
                >
                  {filteredOptions.length ? (
                    filteredOptions.map((option, index) => (
                      <button
                        key={`${String(option.value)}-${option.label}-${index}`}
                        type="button"
                        onClick={() => {
                          field.onChange(option.value);
                          setQuery(option.label);
                          setIsTyping(false);
                          setOpen(false);
                          onOptionSelect?.(option);
                        }}
                        className={clsx(
                          "block w-full px-4 py-3 text-left text-sm transition",
                          getThemeClass(
                            selectClasses.textColor || inputClasses.textColor,
                            "text-theme-text"
                          ),
                          getThemeClass(
                            selectClasses.optionHoverBgColor,
                            "hover:bg-theme-surface-soft"
                          ),
                          selectClasses.option
                        )}
                      >
                        {option.label}
                      </button>
                    ))
                  ) : (
                    <div
                      className={clsx(
                        "px-4 py-3 text-sm",
                        getThemeClass(
                          selectClasses.placeholderColor || inputClasses.placeholderColor,
                          "text-theme-text-soft"
                        )
                      )}
                    >
                      {noResultsText}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </WrapperInput>
        );
      }}
    />
  );
}
